import React, { useMemo } from "react";
import { useState } from "react";
import { cn } from "@/lib/utils";
interface DottedBackgroundProps {
  // Style props
  /**
   * Color of the dots in the pattern. Can be any valid CSS color value.
   * @default "#215769"
   */
  dotColor?: string;

  /**
   * Background color behind the dots. Special values: "transparent", "background" (uses CSS variable).
   * @default "transparent"
   */
  backgroundColor?: string;

  /**
   * Size of each dot in pixels. Controls dot diameter.
   * @min 1
   * @max 20
   * @default 2
   */
  dotSize?: number;

  /**
   * Spacing between dots in pixels. Controls the grid spacing.
   * @min 5
   * @max 50
   * @default 10
   */
  dotSpacing?: number;

  // Effects props
  /**
   * Enable/disable the vignette darkening effect around edges.
   * @default true
   */
  enableVignette?: boolean;

  /**
   * Color of the vignette effect. Can be any valid CSS color value.
   * @default "rgb(0,0,0)"
   */
  vignetteColor?: string;

  /**
   * Enable/disable the inner glow effect from the center.
   * @default true
   */
  enableInnerGlow?: boolean;

  /**
   * Color of the inner glow effect. Can be any valid CSS color value.
   * @default "rgb(0,0,0)"
   */
  innerGlowColor?: string;

  /**
   * Enable/disable the ripple animation effect. Animates on hover.
   * @default false
   */
  enableRipple?: boolean;

  // Container props
  /**
   * Additional CSS classes to apply to the container.
   * @default ""
   */
  className?: string;

  /**
   * Additional inline styles to apply to the container.
   * @default {}
   */
  style?: React.CSSProperties;
}
const DottedVignetteBackground: React.FC<DottedBackgroundProps> = ({
  dotColor = "#215769",
  backgroundColor = "transparent",
  dotSize = 2,
  dotSpacing = 10,
  enableVignette = true,
  vignetteColor = "rgb(0,0,0)",
  enableInnerGlow = true,
  innerGlowColor = "rgb(0,0,0)",
  enableRipple = false,
  className = "",
  style = {}
}) => {
  // Helper function to resolve color values
  const resolveColor = (color: string): string => {
    switch (color.toLowerCase()) {
      case "transparent":
        return "transparent";
      case "background":
        return "hsl(var(--background))";
      default:
        return color;
    }
  };

  // Resolve all color values
  const resolvedBackgroundColor = resolveColor(backgroundColor);
  const resolvedVignetteColor = resolveColor(vignetteColor);
  const resolvedInnerGlowColor = resolveColor(innerGlowColor);

  // Create dot background using CSS radial gradients
  const dotBackground = `radial-gradient(circle at center, ${dotColor} ${dotSize}px, transparent ${dotSize}px)`;
  return <div className={cn("absolute inset-0 h-full w-full overflow-hidden", className)} style={style} data-sentry-component="DottedVignetteBackground" data-sentry-source-file="dotted-vignette-background.tsx">
      {/* Base dots layer */}
      <div className="absolute inset-0" style={{
      backgroundColor: resolvedBackgroundColor,
      backgroundImage: dotBackground,
      backgroundSize: `${dotSpacing}px ${dotSpacing}px`,
      backgroundPosition: "center center"
    }} />

      {/* Ripple effect layers - scaled from center */}
      {enableRipple && <div className="ripple-container absolute inset-0">
          {Array.from({
        length: 8
      }, (_, i) => <div key={i} className={`ripple-wave absolute inset-0`} style={{
        backgroundImage: dotBackground,
        backgroundSize: `${dotSpacing}px ${dotSpacing}px`,
        backgroundPosition: "center center",
        transformOrigin: "center center",
        transform: "scale(0)",
        opacity: 0,
        animation: `rippleWave 3s infinite ease-out`,
        animationDelay: `${i * 0.2}s`
      }} />)}
        </div>}

      {/* Vignette overlay */}
      {enableVignette && <div className="pointer-events-none absolute inset-0" style={{
      background: `radial-gradient(circle at 50% 50%, transparent 40%, ${resolvedVignetteColor} 100%)`,
      opacity: 0.9
    }} />}

      {/* Inner glow overlay */}
      {enableInnerGlow && <div className="pointer-events-none absolute inset-0" style={{
      background: `radial-gradient(circle at 50% 50%, ${resolvedInnerGlowColor} 0%, transparent 25%)`,
      opacity: 0.7
    }} />}

      <style jsx>{`
        @keyframes rippleWave {
          0% {
            transform: scale(0);
            opacity: 1;
          }
          25% {
            transform: scale(0.5);
            opacity: 0.8;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.6;
          }
          75% {
            transform: scale(2);
            opacity: 0.3;
          }
          100% {
            transform: scale(3);
            opacity: 0;
          }
        }

        .ripple-container:hover .ripple-wave {
          animation-play-state: running;
        }

        .ripple-wave {
          animation-play-state: paused;
        }
      `}</style>
    </div>;
};
export { DottedVignetteBackground };